/**
 * SuggestedTopicsPanel — iter100.46d
 *
 * A local-SEO coaching card that surfaces evergreen article topics the
 * doctor hasn't claimed yet on their landing page. Turns the Topics tab
 * from a passive list into an active marketing platform.
 *
 * Rendered inside the LandingPageEditor's Topics tab (moved out of the
 * Articles tab in iter100.46d so each tab has one clear job).
 * Suggestions are fetched from `GET /api/doctor/content/suggested-topics`.
 *
 * Interactions:
 *   • Draft this → navigates (same tab) to
 *     `/doctor/content/article/new?from_topic={id}`. The dedicated
 *     ContentEditorPage creates the draft server-side on mount and
 *     drops the doctor straight into the full-page editor with the
 *     outline pre-filled.
 *   • Dismiss (×) → POST /suggested-topics/{id}/dismiss.
 *   • Show dismissed → local state toggle that also lists dismissed topics
 *     with a Restore button.
 *
 * When the doctor returns from the editor (via the Back-to-Articles pill),
 * this tab remounts and refetches naturally — no cross-tab plumbing
 * required.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Lightbulb, MagicWand, X, CaretDown, CaretUp,
    Sparkle, ArrowClockwise, TrendUp,
} from "@phosphor-icons/react";

const CATEGORY_COLORS = {
    "Cost & value":     "bg-emerald-50 text-emerald-800 border-emerald-200",
    "Comparison":       "bg-indigo-50 text-indigo-800 border-indigo-200",
    "Audience":         "bg-rose-50 text-rose-800 border-rose-200",
    "Explainer":        "bg-sky-50 text-sky-800 border-sky-200",
    "Occasion":         "bg-amber-50 text-amber-800 border-amber-200",
    "Retention":        "bg-purple-50 text-purple-800 border-purple-200",
    "First visit":      "bg-teal-50 text-teal-800 border-teal-200",
    "Support":          "bg-orange-50 text-orange-800 border-orange-200",
    "Outcomes":         "bg-lime-50 text-lime-800 border-lime-200",
    "Lifestyle":        "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200",
    "Concern":          "bg-red-50 text-red-800 border-red-200",
    "Brand":            "bg-slate-100 text-slate-800 border-slate-200",
};

const PRIORITY_LABEL = { 1: "High demand", 2: "High demand", 3: "Medium demand", 4: "Long tail", 5: "Long tail" };

export default function SuggestedTopicsPanel() {
    const [state, setState] = useState({ loading: true, city: "", topics: [], error: null });
    const [showDismissed, setShowDismissed] = useState(false);
    const [dismissedTopics, setDismissedTopics] = useState([]);
    // iter100.60 — Category filter bar. `null` means "All". Chip counts are
    // derived from the current (non-dismissed) topic list so the bar always
    // reflects what's actually filterable right now.
    const [activeCategory, setActiveCategory] = useState(null);

    const load = useCallback(async () => {
        setState((s) => ({ ...s, loading: true, error: null }));
        try {
            const { data } = await api.get("/doctor/content/suggested-topics");
            setState({ loading: false, city: data.city || "", topics: data.topics || [], error: null });
        } catch (e) {
            setState({ loading: false, city: "", topics: [], error: e?.response?.data?.detail || "Could not load suggestions" });
        }
    }, []);
    useEffect(() => { load(); }, [load]);

    // Category buckets — build in the natural order they first appear so
    // popular categories (Cost, Comparison, etc.) surface early. Doctors
    // with sparse topic data still see a clean, honest bar.
    const categoryCounts = useMemo(() => {
        const map = new Map();
        for (const t of state.topics) {
            const key = t.category || "Other";
            map.set(key, (map.get(key) || 0) + 1);
        }
        return Array.from(map.entries()); // [[label, count], ...]
    }, [state.topics]);

    const visibleTopics = activeCategory
        ? state.topics.filter((t) => (t.category || "Other") === activeCategory)
        : state.topics;

    // Reset the filter automatically if the chosen category empties out
    // (e.g. doctor drafted or dismissed every topic in it). Prevents the
    // grid from ever showing "no results" without an obvious escape hatch.
    useEffect(() => {
        if (activeCategory && !categoryCounts.some(([label]) => label === activeCategory)) {
            setActiveCategory(null);
        }
    }, [activeCategory, categoryCounts]);

    const dismiss = async (topic) => {
        try {
            await api.post(`/doctor/content/suggested-topics/${topic.id}/dismiss`);
            setState((s) => ({ ...s, topics: s.topics.filter((t) => t.id !== topic.id) }));
            setDismissedTopics((arr) => [...arr, topic]);
        } catch {
            toast.error("Could not dismiss");
        }
    };

    const restore = async (topicId) => {
        try {
            await api.post(`/doctor/content/suggested-topics/${topicId}/restore`);
            await load();
            setDismissedTopics((arr) => arr.filter((t) => t.id !== topicId));
        } catch {
            toast.error("Could not restore");
        }
    };

    return (
        <div
            className="rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-white shadow-sm overflow-hidden"
            data-testid="suggested-topics-panel"
        >
            {/* Header */}
            <div className="p-4 sm:p-5 flex items-start gap-3 border-b border-amber-100">
                <div className="h-10 w-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                    <Lightbulb size={20} weight="fill" className="text-amber-600" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-base font-bold text-slate-900" data-testid="suggested-topics-heading">
                            Suggested topics for you
                        </h3>
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-md border bg-white border-amber-200 text-amber-800">
                            <Sparkle size={9} weight="fill" /> SEO coaching
                        </span>
                    </div>
                    <p className="text-[12.5px] text-slate-600 mt-0.5 leading-relaxed">
                        {state.city
                            ? <>Evergreen topics with strong local search demand in <b>{state.city}</b>. Draft one, edit it, and it goes live on your landing page.</>
                            : <>Add a city on the Profile tab to unlock local-SEO topic suggestions tailored to your area.</>
                        }
                    </p>
                </div>
            </div>

            {/* iter100.60 — Category filter chip bar. Only shown once
                we have at least 2 categories to filter across
                (single-category doctors don't need a chooser). */}
            {!state.loading && !state.error && categoryCounts.length >= 2 && (
                <div
                    className="px-4 pt-3 pb-2 flex flex-wrap items-center gap-1.5 border-b border-amber-100 bg-white/60"
                    data-testid="suggested-topics-filter-bar"
                >
                    <FilterChip
                        label="All"
                        count={state.topics.length}
                        active={activeCategory === null}
                        onClick={() => setActiveCategory(null)}
                        testid="suggested-filter-all"
                    />
                    {categoryCounts.map(([label, count]) => (
                        <FilterChip
                            key={label}
                            label={label}
                            count={count}
                            active={activeCategory === label}
                            onClick={() => setActiveCategory(activeCategory === label ? null : label)}
                            tone={CATEGORY_COLORS[label]}
                            testid={`suggested-filter-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        />
                    ))}
                </div>
            )}

            {/* Loading / error / empty states */}
            {state.loading && (
                <div className="p-6 text-center text-[13px] text-muted-foreground" data-testid="suggested-topics-loading">
                    Fetching suggestions…
                </div>
            )}
            {!state.loading && state.error && (
                <div className="p-4 text-[13px] text-red-800 bg-red-50 border-t border-red-200" data-testid="suggested-topics-error">
                    {state.error}
                    <button onClick={load} className="ml-2 underline">Retry</button>
                </div>
            )}
            {!state.loading && !state.error && visibleTopics.length === 0 && dismissedTopics.length === 0 && (
                <div className="p-6 text-center text-[13px] text-slate-500" data-testid="suggested-topics-empty">
                    You&apos;ve claimed every suggested topic. Nicely done!
                </div>
            )}

            {/* Cards */}
            {!state.loading && !state.error && visibleTopics.length > 0 && (
                <div className="p-3 sm:p-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="suggested-topics-grid">
                    {visibleTopics.map((t) => (
                        <TopicCard
                            key={t.id}
                            topic={t}
                            onDismiss={() => dismiss(t)}
                        />
                    ))}
                </div>
            )}

            {/* Dismissed drawer */}
            {(dismissedTopics.length > 0 || showDismissed) && (
                <div className="border-t border-amber-100 bg-amber-50/40">
                    <button
                        type="button"
                        onClick={() => setShowDismissed((v) => !v)}
                        className="w-full px-4 py-2 flex items-center justify-between text-[12px] font-semibold text-slate-700 hover:bg-amber-50/70"
                        data-testid="suggested-topics-dismissed-toggle"
                    >
                        <span className="inline-flex items-center gap-1.5">
                            <ArrowClockwise size={12} weight="bold" />
                            {showDismissed ? "Hide" : "Show"} dismissed
                            <span className="ml-1 h-4 min-w-[16px] px-1 rounded bg-white border border-amber-200 text-[10px] flex items-center justify-center">
                                {dismissedTopics.length}
                            </span>
                        </span>
                        {showDismissed ? <CaretUp size={12} /> : <CaretDown size={12} />}
                    </button>
                    {showDismissed && (
                        <div className="px-4 pb-4 space-y-1.5" data-testid="suggested-topics-dismissed-list">
                            {dismissedTopics.length === 0 && (
                                <div className="text-[12px] text-slate-500 italic">Nothing dismissed yet in this session.</div>
                            )}
                            {dismissedTopics.map((t) => (
                                <div key={t.id} className="flex items-center justify-between gap-2 text-[12.5px] py-1 border-b border-amber-100 last:border-0">
                                    <span className="text-slate-700 truncate">{t.title}</span>
                                    <button
                                        onClick={() => restore(t.id)}
                                        className="shrink-0 text-[11px] font-semibold text-primary hover:underline"
                                        data-testid={`suggested-restore-${t.id}`}
                                    >
                                        Restore
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


function TopicCard({ topic, onDismiss }) {
    const catClasses = CATEGORY_COLORS[topic.category] || "bg-slate-100 text-slate-800 border-slate-200";
    const [keywordsOpen, setKeywordsOpen] = useState(false);
    // iter100.46 (post-review) — Draft this opens a real link in a new tab
    // so the browser preserves the user-gesture context and popup
    // blockers stay out of our way. The target page (ContentEditorPage)
    // detects `?from_topic` on mount, POSTs to /from-topic, and lands
    // the doctor directly inside the full-page editor.
    const draftHref = `/doctor/content/article/new?from_topic=${encodeURIComponent(topic.id)}`;

    return (
        <div
            className="relative rounded-xl border border-slate-200 bg-white p-4 flex flex-col hover:border-slate-300 hover:shadow-sm transition-all"
            data-testid={`suggested-topic-${topic.id}`}
        >
            {/* Dismiss (top-right) */}
            <button
                type="button"
                onClick={onDismiss}
                className="absolute top-2.5 right-2.5 h-6 w-6 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 inline-flex items-center justify-center transition-colors"
                title="Dismiss this suggestion"
                data-testid={`suggested-dismiss-${topic.id}`}
            >
                <X size={12} weight="bold" />
            </button>

            {/* Chips row */}
            <div className="flex items-center gap-1.5 mb-2 flex-wrap pr-6">
                <span className={`inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${catClasses}`}>
                    {topic.category}
                </span>
                <span className="inline-flex items-center gap-1 h-5 px-1.5 rounded-md text-[10px] font-bold uppercase tracking-wider border bg-slate-50 border-slate-200 text-slate-600">
                    <TrendUp size={9} weight="fill" /> {PRIORITY_LABEL[topic.priority] || "Long tail"}
                </span>
            </div>

            {/* Title */}
            <div className="font-display text-[14.5px] font-bold text-slate-900 leading-snug mb-1.5" data-testid={`suggested-title-${topic.id}`}>
                {topic.title}
            </div>

            {/* Summary preview */}
            <p className="text-[12.5px] text-slate-600 leading-relaxed line-clamp-3 mb-2">
                {topic.summary}
            </p>

            {/* Why + keywords toggle */}
            <div className="mt-auto text-[11.5px] text-slate-500 italic border-t border-slate-100 pt-2">
                <span className="font-semibold not-italic text-slate-700">Why:</span> {topic.why}
            </div>
            {topic.keywords?.length > 0 && (
                <div className="mt-1.5">
                    <button
                        type="button"
                        onClick={() => setKeywordsOpen((v) => !v)}
                        className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-slate-800"
                        data-testid={`suggested-keywords-toggle-${topic.id}`}
                    >
                        {keywordsOpen ? "Hide" : "Show"} target keywords ({topic.keywords.length})
                        {keywordsOpen ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />}
                    </button>
                    {keywordsOpen && (
                        <div className="mt-1.5 flex flex-wrap gap-1" data-testid={`suggested-keywords-${topic.id}`}>
                            {topic.keywords.map((k) => (
                                <code key={k} className="text-[10.5px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200">
                                    {k}
                                </code>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* Action — same-tab navigation to the full editor */}
            <div className="mt-3 flex items-center justify-end">
                <Button
                    asChild
                    size="sm"
                    className="h-8 rounded-full font-semibold text-[12px]"
                    data-testid={`suggested-draft-${topic.id}`}
                >
                    <Link to={draftHref}>
                        <MagicWand size={12} weight="fill" />
                        Draft this
                    </Link>
                </Button>
            </div>
        </div>
    );
}

/* iter100.60 — Single filter chip. Active state uses the category's own
   colour palette (drawn from CATEGORY_COLORS) so it visually matches the
   badge on the card grid below. Inactive chips get a neutral slate look
   so the active chip clearly stands out. */
function FilterChip({ label, count, active, onClick, tone, testid }) {
    const activeClasses = tone
        ? `${tone} shadow-sm`
        : "bg-slate-900 text-white border-slate-900 shadow-sm";
    const inactiveClasses =
        "bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-900";
    return (
        <button
            type="button"
            onClick={onClick}
            aria-pressed={active}
            className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11.5px] font-semibold border transition-colors ${
                active ? activeClasses : inactiveClasses
            }`}
            data-testid={testid}
        >
            <span>{label}</span>
            <span
                className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold ${
                    active ? "bg-white/60 text-slate-900" : "bg-slate-100 text-slate-700"
                }`}
            >
                {count}
            </span>
        </button>
    );
}
